import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { exportSizeText } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, NumberField, SelectField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  SERVICES,
  dateText,
  daysAgo,
  deleteInvoice,
  endLicence,
  invoiceIssues,
  loadAudit,
  loadTenantDetail,
  moneyText,
  saveInvoice,
  saveLicence,
  setSwitch,
  setTenantStatus,
  type AdminTenant,
  type AuditRow,
  type Invoice,
  type InvoiceDraft,
  type Licence,
  type TenantDetail,
} from './api.ts';
import { AuditTable } from './AdminAuditScreen.tsx';
import { SyncVersionChip, belowMinimum } from './AdminDevicesScreen.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
type Notice = { readonly ok: boolean; readonly text: string } | null;

export interface AdminTenantDetailProps {
  readonly sb: SupabaseClient;
  readonly tenant: AdminTenant;
  readonly onBack: () => void;
  readonly onChanged: () => void;
}

/** One organisation in Braynewave's console: status, licence, seats and devices, switches, invoices, exports, audit. */
export function AdminTenantDetail({ sb, tenant, onBack, onChanged }: AdminTenantDetailProps): ReactElement {
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([loadTenantDetail(sb, tenant.id), loadAudit(sb, { tenantId: tenant.id, limit: 20 })])
      .then(([d, a]) => {
        setDetail(d);
        setAudit(a);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenant.id]);
  useEffect(reload, [reload]);

  /** Run a change, say what happened, and read everything again. */
  const run = async (action: () => Promise<void>, done: string): Promise<void> => {
    try {
      await action();
      setNotice({ ok: true, text: done });
      onChanged();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    }
    reload();
  };

  return (
    <section className="page">
      <PageHeader
        title={tenant.name}
        subtitle={`${tenant.slug} · ${tenant.region} · added ${dateText(tenant.createdAt)}`}
      >
        <Button variant="quiet" onClick={onBack}>
          All tenants
        </Button>
      </PageHeader>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <StatusPanel
        tenant={tenant}
        onSave={(status) =>
          run(() => setTenantStatus(sb, tenant.id, status), `${tenant.name} is now ${status}.`)
        }
      />
      {error && <ErrorState message={error} onRetry={reload} />}
      {!detail && !error && <Loading what="the organisation" />}
      {detail && (
        <>
          <LicencePanel
            licence={detail.licence}
            seatsInUse={detail.seats.length}
            onSave={(l) => run(() => saveLicence(sb, tenant.id, l), 'Licence saved.')}
          />
          <SeatsPanel
            detail={detail}
            onEnd={(what, reason, note, label) =>
              run(
                () => endLicence(sb, what, reason, note),
                `${label} ${reason === 'revoked' ? 'revoked' : 'released'}.`,
              )
            }
          />
          <SwitchesPanel
            detail={detail}
            onSet={(service, on, note, label) =>
              run(
                () => setSwitch(sb, tenant.id, service, on, note),
                `${label} switched ${on ? 'on' : 'off'}.`,
              )
            }
          />
          <InvoicesPanel
            invoices={detail.invoices}
            onSave={(d, id) => run(() => saveInvoice(sb, tenant.id, d, id), `Invoice ${d.ref.trim()} saved.`)}
            onDelete={(inv) => run(() => deleteInvoice(sb, inv.id), `Invoice ${inv.ref} deleted.`)}
          />
          <div className="panel stack">
            <h2 className="heading">Nightly exports</h2>
            <p className="caption muted">
              Braynewave sees that the exports ran, never what is in them. The organisation’s tenant admins
              download them on their Data exports page.
            </p>
            {detail.exports.length === 0 ? (
              <p className="body muted">No exports yet.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="label">Day</th>
                      <th className="label">Kind</th>
                      <th className="label">Result</th>
                      <th className="label">Rows</th>
                      <th className="label">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.exports.map((x) => (
                      <tr key={x.id}>
                        <td className="data">{x.day}</td>
                        <td>{x.kind === 'nightly' ? 'Nightly' : 'Asked for'}</td>
                        <td>
                          {x.status === 'failed'
                            ? `Failed: ${x.error ?? ''}`
                            : x.status === 'done'
                              ? 'Done'
                              : 'Waiting'}
                          {x.pruned ? (
                            <span className="caption muted"> · removed (older than the newest 14)</span>
                          ) : null}
                        </td>
                        <td className="data">{x.rows ?? '–'}</td>
                        <td className="data">{x.bytes === null ? '–' : exportSizeText(x.bytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="panel stack">
            <h2 className="heading">Recent changes</h2>
            {audit && <AuditTable rows={audit} showTenant={false} />}
          </div>
        </>
      )}
    </section>
  );
}

function StatusPanel({
  tenant,
  onSave,
}: {
  readonly tenant: AdminTenant;
  readonly onSave: (s: AdminTenant['status']) => Promise<void>;
}): ReactElement {
  const [status, setStatus] = useState(tenant.status);
  return (
    <div className="panel stack">
      <h2 className="heading">Organisation</h2>
      <div className="form-grid">
        <SelectField
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            ['active', 'Active'],
            ['suspended', 'Suspended'],
            ['closed', 'Closed (no more nightly exports)'],
          ]}
        />
      </div>
      <div className="row">
        <Button onClick={() => void onSave(status)} disabled={status === tenant.status}>
          Save status
        </Button>
      </div>
    </div>
  );
}

function LicencePanel({
  licence,
  seatsInUse,
  onSave,
}: {
  readonly licence: Licence | null;
  readonly seatsInUse: number;
  readonly onSave: (l: Licence) => Promise<void>;
}): ReactElement {
  const [plan, setPlan] = useState(licence?.plan ?? 'standard');
  const [seats, setSeats] = useState<number | null>(licence?.seats ?? 3);
  const [laptops, setLaptops] = useState<number | null>(licence?.laptopsPerUser ?? 1);
  const [mobiles, setMobiles] = useState<number | null>(licence?.mobilesPerUser ?? 2);
  const [features, setFeatures] = useState((licence?.features ?? []).join(', '));
  const [status, setStatus] = useState<Licence['status']>(licence?.status ?? 'active');
  const [ends, setEnds] = useState(licence?.subscriptionEnds ?? '');
  const [notes, setNotes] = useState(licence?.notes ?? '');
  const [issue, setIssue] = useState<string | null>(null);

  function submit(e: FormEvent): void {
    e.preventDefault();
    const list = features
      .split(',')
      .map((f) => f.trim().toLowerCase())
      .filter(Boolean);
    if (!/^[a-z0-9_]{1,40}$/.test(plan)) return setIssue('The plan is lower-case letters, digits and _.');
    if (seats === null || !Number.isInteger(seats) || seats < 0 || seats > 10000)
      return setIssue('Seats are a whole number from 0 to 10,000.');
    for (const n of [laptops, mobiles])
      if (n === null || !Number.isInteger(n) || n < 0 || n > 10)
        return setIssue('Devices per person are 0 to 10.');
    if (list.some((f) => !/^[a-z0-9_.-]{1,40}$/.test(f)))
      return setIssue('Features are lower-case names separated by commas.');
    setIssue(null);
    void onSave({
      plan,
      seats,
      laptopsPerUser: laptops!,
      mobilesPerUser: mobiles!,
      features: list,
      status,
      subscriptionEnds: ends || null,
      notes,
    });
  }

  return (
    <form className="panel stack" onSubmit={submit} noValidate aria-label="Licence">
      <h2 className="heading">Licence</h2>
      <p className="body">
        {licence
          ? `${seatsInUse} of ${licence.seats} seats in use${seatsInUse > licence.seats ? ': more than licensed, so new people are refused until seats are released' : ''}.`
          : 'No licence yet: Teslyn Field refuses to activate until one is saved.'}
      </p>
      <div className="form-grid">
        <TextField label="Plan" spellCheck={false} value={plan} onChange={setPlan} />
        <NumberField label="Seats (licensed staff)" value={seats} onChange={setSeats} step="1" />
        <NumberField label="Laptops per person" value={laptops} onChange={setLaptops} step="1" />
        <NumberField label="Mobiles per person" value={mobiles} onChange={setMobiles} step="1" />
        <SelectField
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            ['active', 'Active'],
            ['cancelled', 'Cancelled (laptops stop at their hard stop)'],
          ]}
        />
        <TextField label="Subscription ends" type="date" value={ends} onChange={setEnds} />
      </div>
      <TextField
        label="Features"
        spellCheck={false}
        hint="Carried in the licence; nothing is gated until plans arrive"
        value={features}
        onChange={setFeatures}
      />
      <TextArea
        label="Notes"
        hint="The organisation’s tenant admins can read these notes."
        value={notes}
        onChange={setNotes}
        rows={3}
      />
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary">
          Save licence
        </Button>
      </div>
    </form>
  );
}

type EndTarget = {
  readonly what: { readonly seat: string } | { readonly device: string };
  readonly label: string;
};

function SeatsPanel({
  detail,
  onEnd,
}: {
  readonly detail: TenantDetail;
  readonly onEnd: (
    what: EndTarget['what'],
    reason: 'released' | 'revoked',
    note: string,
    label: string,
  ) => Promise<void>;
}): ReactElement {
  const [ending, setEnding] = useState<(EndTarget & { readonly reason: 'released' | 'revoked' }) | null>(
    null,
  );
  const [note, setNote] = useState('');
  const ask = (t: EndTarget, reason: 'released' | 'revoked'): void => {
    setNote('');
    setEnding({ ...t, reason });
  };
  return (
    <div className="panel stack">
      <h2 className="heading">Seats and devices</h2>
      {detail.seats.length === 0 ? (
        <p className="body muted">Nobody holds a seat yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Person</th>
                <th className="label">Last check-in</th>
                <th className="label">Devices</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {detail.seats.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.name}
                    {s.email && (
                      <>
                        <br />
                        <span className="caption muted data">{s.email}</span>
                      </>
                    )}
                  </td>
                  <td className="caption">{daysAgo(s.lastCheckinAt)}</td>
                  <td>
                    {s.devices.length === 0 ? (
                      <span className="muted">Web only</span>
                    ) : (
                      <ul className="stack">
                        {s.devices.map((d) => (
                          <li key={d.id} className="row">
                            <span className="body">
                              {d.kind === 'laptop' ? 'Laptop' : 'Mobile'} {d.label || '(no name)'}{' '}
                              <span className="caption muted">· {daysAgo(d.lastCheckinAt)}</span>{' '}
                              <span className="caption muted data">
                                · {d.appVersion ?? 'version not reported'}
                              </span>
                            </span>
                            {d.kind === 'laptop' && belowMinimum(d, detail.minSyncVersion) && (
                              <SyncVersionChip below />
                            )}
                            <Button
                              variant="quiet"
                              aria-label={`Release ${d.label || 'device'} of ${s.name}`}
                              onClick={() =>
                                ask({ what: { device: d.id }, label: d.label || 'Device' }, 'released')
                              }
                            >
                              Release
                            </Button>
                            <Button
                              variant="quiet"
                              aria-label={`Revoke ${d.label || 'device'} of ${s.name}`}
                              onClick={() =>
                                ask({ what: { device: d.id }, label: d.label || 'Device' }, 'revoked')
                              }
                            >
                              Revoke
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="table__actions">
                    <Button
                      variant="quiet"
                      aria-label={`Release the seat of ${s.name}`}
                      onClick={() => ask({ what: { seat: s.id }, label: `Seat of ${s.name}` }, 'released')}
                    >
                      Release seat
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ending && (
        <div className="panel confirm stack" role="dialog" aria-labelledby="end-title">
          <p id="end-title" className="body">
            {ending.reason === 'revoked'
              ? `Revoke ${ending.label}? Use this for a lost or stolen laptop: it is read only from its next start once it checks in, and at its hard stop if it never does.`
              : `Release ${ending.label}? The slot is free at once; the device is read only from its next start.`}
          </p>
          <label className="label muted" htmlFor="end-note">
            Note for the audit log
          </label>
          <input
            spellCheck
            id="end-note"
            className="input"
            value={note}
            maxLength={300}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="row">
            <Button
              className="btn--danger"
              onClick={() => {
                const e = ending;
                setEnding(null);
                void onEnd(e.what, e.reason, note, e.label);
              }}
            >
              {ending.reason === 'revoked' ? 'Revoke' : 'Release'}
            </Button>
            <Button variant="quiet" onClick={() => setEnding(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SwitchesPanel({
  detail,
  onSet,
}: {
  readonly detail: TenantDetail;
  readonly onSet: (service: string, on: boolean, note: string, label: string) => Promise<void>;
}): ReactElement {
  return (
    <div className="panel stack">
      <h2 className="heading">Service switches</h2>
      {SERVICES.map((svc) => (
        <ServiceRow
          key={svc.id}
          svc={svc}
          current={detail.switches.find((s) => s.service === svc.id)}
          onSet={onSet}
        />
      ))}
    </div>
  );
}

function ServiceRow({
  svc,
  current,
  onSet,
}: {
  readonly svc: (typeof SERVICES)[number];
  readonly current: TenantDetail['switches'][number] | undefined;
  readonly onSet: (service: string, on: boolean, note: string, label: string) => Promise<void>;
}): ReactElement {
  const [on, setOn] = useState(current?.enabled ?? false);
  const [note, setNote] = useState(current?.note ?? '');
  const changed = on !== (current?.enabled ?? false) || note !== (current?.note ?? '');
  return (
    <div className="stack" role="group" aria-label={svc.label}>
      <CheckField label={svc.label} checked={on} onChange={setOn} />
      <p className="caption muted">
        {svc.about} {current ? `Last changed ${dateText(current.updatedAt)}.` : 'Off (never set).'}
      </p>
      <TextField label={`Note for ${svc.label}`} value={note} onChange={setNote} />
      <div className="row">
        <Button onClick={() => void onSet(svc.id, on, note, svc.label)} disabled={!changed}>
          Save switch
        </Button>
      </div>
    </div>
  );
}

const EMPTY_DRAFT: InvoiceDraft = {
  ref: '',
  issuedOn: '',
  periodFrom: '',
  periodTo: '',
  amount: null,
  gst: 0,
  currency: 'AUD',
  status: 'issued',
  paidOn: '',
  notes: '',
};

const draftOf = (i: Invoice): InvoiceDraft => ({
  ref: i.ref,
  issuedOn: i.issuedOn,
  periodFrom: i.periodFrom ?? '',
  periodTo: i.periodTo ?? '',
  amount: i.amount,
  gst: i.gst,
  currency: i.currency,
  status: i.status,
  paidOn: i.paidOn ?? '',
  notes: i.notes,
});

export const INVOICE_STATUS: Record<Invoice['status'], string> = {
  issued: 'Not paid yet',
  paid: 'Paid',
  void: 'Void',
};

function InvoicesPanel({
  invoices,
  onSave,
  onDelete,
}: {
  readonly invoices: readonly Invoice[];
  readonly onSave: (d: InvoiceDraft, id: string | null) => Promise<void>;
  readonly onDelete: (i: Invoice) => Promise<void>;
}): ReactElement {
  const [editing, setEditing] = useState<{ readonly id: string | null; readonly draft: InvoiceDraft } | null>(
    null,
  );
  const [confirmDelete, setConfirmDelete] = useState<Invoice | null>(null);
  return (
    <div className="panel stack">
      <h2 className="heading">Invoices</h2>
      <p className="caption muted">
        Invoices are sent by hand; these records say what was invoiced, when and whether it is paid. The
        organisation’s tenant admins can read them, notes included.
      </p>
      {invoices.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Invoice</th>
                <th className="label">Issued</th>
                <th className="label">Period</th>
                <th className="label">Amount</th>
                <th className="label">Paid</th>
                <th className="label">Notes</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td className="data">{i.ref}</td>
                  <td>{dateText(i.issuedOn)}</td>
                  <td>
                    {i.periodFrom || i.periodTo
                      ? `${dateText(i.periodFrom)} to ${dateText(i.periodTo)}`
                      : '–'}
                  </td>
                  <td className="data">{moneyText(i.amount, i.currency)}</td>
                  <td>{i.status === 'paid' ? `Paid ${dateText(i.paidOn)}` : INVOICE_STATUS[i.status]}</td>
                  <td className="caption">{i.notes}</td>
                  <td className="table__actions">
                    <Button
                      variant="quiet"
                      aria-label={`Edit invoice ${i.ref}`}
                      onClick={() => setEditing({ id: i.id, draft: draftOf(i) })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="quiet"
                      aria-label={`Delete invoice ${i.ref}`}
                      onClick={() => setConfirmDelete(i)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirmDelete && (
        <div className="panel confirm" role="dialog" aria-labelledby="delete-invoice-title">
          <p id="delete-invoice-title" className="body">
            Delete invoice {confirmDelete.ref}? Use Void instead for an invoice that was sent and cancelled.
            The deletion is recorded.
          </p>
          <div className="row">
            <Button
              className="btn--danger"
              onClick={() => {
                const i = confirmDelete;
                setConfirmDelete(null);
                void onDelete(i);
              }}
            >
              Delete invoice
            </Button>
            <Button variant="quiet" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {editing ? (
        <InvoiceForm
          draft={editing.draft}
          onCancel={() => setEditing(null)}
          onSave={async (d) => {
            await onSave(d, editing.id);
            setEditing(null);
          }}
        />
      ) : (
        <div className="row">
          <Button onClick={() => setEditing({ id: null, draft: EMPTY_DRAFT })}>Record an invoice</Button>
        </div>
      )}
    </div>
  );
}

function InvoiceForm({
  draft,
  onSave,
  onCancel,
}: {
  readonly draft: InvoiceDraft;
  readonly onSave: (d: InvoiceDraft) => Promise<void>;
  readonly onCancel: () => void;
}): ReactElement {
  const [d, setD] = useState<InvoiceDraft>(draft);
  const [issues, setIssues] = useState<string[]>([]);
  const set =
    <K extends keyof InvoiceDraft>(k: K) =>
    (v: InvoiceDraft[K]): void =>
      setD((x) => ({ ...x, [k]: v }));
  return (
    <form
      className="stack"
      aria-label="Invoice"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const found = invoiceIssues(d);
        setIssues(found);
        if (found.length === 0) void onSave(d);
      }}
    >
      <div className="form-grid">
        <TextField label="Invoice number" spellCheck={false} value={d.ref} onChange={set('ref')} autoFocus />
        <TextField label="Issued" type="date" value={d.issuedOn} onChange={set('issuedOn')} />
        <TextField label="Period from" type="date" value={d.periodFrom} onChange={set('periodFrom')} />
        <TextField label="Period to" type="date" value={d.periodTo} onChange={set('periodTo')} />
        <NumberField
          label="Amount"
          value={d.amount}
          onChange={set('amount')}
          step="0.01"
          hint="The total invoiced, including any GST."
        />
        <NumberField label="GST included" value={d.gst} onChange={set('gst')} step="0.01" />
        <TextField
          label="Currency"
          spellCheck={false}
          value={d.currency}
          onChange={(v) => set('currency')(v.toUpperCase())}
        />
        <SelectField
          label="Paid"
          value={d.status}
          onChange={set('status')}
          options={[
            ['issued', INVOICE_STATUS.issued],
            ['paid', INVOICE_STATUS.paid],
            ['void', INVOICE_STATUS.void],
          ]}
        />
        {d.status === 'paid' && (
          <TextField label="Paid on" type="date" value={d.paidOn} onChange={set('paidOn')} />
        )}
      </div>
      <TextArea label="Notes" value={d.notes} onChange={set('notes')} rows={2} />
      {issues.length > 0 && (
        <ul className="notice notice--fail issues" role="alert">
          {issues.map((i) => (
            <li key={i} className="body">
              {i}
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <Button type="submit" variant="primary">
          Save invoice
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
